import type { FormEvent } from 'react';
import React, { memo, useEffect, useRef, useState } from '../../lib/teact/teact';
import { withGlobal } from '../../global';

import type { ApiWalletTransactionCategory, ApiWalletTransactionCounterparty } from '../../api/types';
import type { Account } from '../../global/types';
import type { MetadataRecordKind } from '../common/MetadataRecordEditor';

import { selectAccounts, selectCurrentAccountId } from '../../global/selectors';
import buildClassName from '../../util/buildClassName';
import { shortenAddress } from '../../util/shortenAddress';
import { isWalletSystemMetadataRecord } from '../../util/walletSystemMetadata';

import useHistoryBack from '../../hooks/useHistoryBack';
import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';
import useScrolledState from '../../hooks/useScrolledState';
import useWalletMetadataDirectory from '../../hooks/useWalletMetadataDirectory';

import CounterpartyAvatar from '../common/CounterpartyAvatar';
import MetadataCategoryIcon from '../common/MetadataCategoryIcon';
import MetadataRecordEditor from '../common/MetadataRecordEditor';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import SettingsHeader from './SettingsHeader';
import SettingsMetadataSharing from './SettingsMetadataSharing';

import receiveStyles from '../receive/ReceiveModal.module.scss';
import searchStyles from '../ui/SearchBar.module.scss';
import tabStyles from '../ui/Tab.module.scss';
import tabListStyles from '../ui/TabList.module.scss';
import settingsStyles from './Settings.module.scss';
import styles from './SettingsMetadata.module.scss';

type OwnProps = {
  isActive?: boolean;
  onBackClick: NoneToVoidFunction;
};
type StateProps = { accountId: string; accounts?: Record<string, Account> };
type MetadataTab = MetadataRecordKind | 'wallets';
type Editor = { kind: MetadataRecordKind; record?: ApiWalletTransactionCounterparty | ApiWalletTransactionCategory };

export function SettingsMetadata({
  isActive, onBackClick, accountId, accounts,
}: OwnProps & StateProps) {
  const lang = useLang();
  const directory = useWalletMetadataDirectory(accountId, accounts, isActive);
  const [tab, setTab] = useState<MetadataTab>('counterparty');
  const [query, setQuery] = useState('');
  const [editor, setEditor] = useState<Editor>();
  const [isEditorBusy, setIsEditorBusy] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const isSavingRef = useRef(false);
  const { isScrolled, handleScroll } = useScrolledState();
  useEffect(() => {
    setEditor(undefined);
    setIsEditorBusy(false);
    setIsConfirmingDelete(false);
    isSavingRef.current = false;
  }, [accountId, isActive]);
  const closeEditor = useLastCallback(() => {
    if (!isSavingRef.current) {
      setEditor(undefined);
      setIsEditorBusy(false);
      setIsConfirmingDelete(false);
    }
  });
  const openEditor = useLastCallback((nextEditor: Editor) => {
    setIsEditorBusy(false);
    setIsConfirmingDelete(false);
    setEditor(nextEditor);
  });
  const handleBack = useLastCallback(() => {
    if (editor) closeEditor();
    else onBackClick();
  });
  useHistoryBack({ isActive, onBack: handleBack });

  const records = (tab === 'counterparty' ? directory.counterparties
    : tab === 'category' ? directory.categories : []).filter((record) => !isWalletSystemMetadataRecord(record));
  const textQuery = query.trim().toLocaleLowerCase();
  const filtered = records.filter((record) => {
    const text = `${record.name} ${'addresses' in record ? record.addresses?.join(' ') : ''}`;
    return text.toLocaleLowerCase().includes(textQuery);
  });
  const title = lang(editor ? editor.kind === 'counterparty' ? 'Counterparty' : 'Category' : '$metadata_directory');

  return (
    <div className={settingsStyles.slide} data-telemetry-block="">
      <SettingsHeader
        title={title}
        isScrolled={isScrolled}
        onBackClick={handleBack}
        endContent={editor?.record?.id && (
          <Button
            isSimple
            isText
            isDisabled={isEditorBusy || isConfirmingDelete}
            className={styles.headerDeleteButton}
            ariaLabel={lang('Delete')}
            onClick={() => setIsConfirmingDelete(true)}
          >
            <i className="icon-trash-small" aria-hidden />
          </Button>
        )}
      />
      <div className={buildClassName(settingsStyles.content, styles.content, 'custom-scroll')} onScroll={handleScroll}>
        {editor ? (
          <MetadataRecordEditor
            key={`${accountId}:${editor.kind}:${editor.record?.id ?? 'new'}`}
            kind={editor.kind}
            accountId={accountId}
            record={editor.record}
            hideDeleteAction
            isDeleteConfirmationOpen={isConfirmingDelete}
            onDeleteConfirmationChange={setIsConfirmingDelete}
            onSaved={closeEditor}
            onCancel={closeEditor}
            onBusyChange={(isSaving) => {
              isSavingRef.current = isSaving;
              setIsEditorBusy(isSaving);
            }}
          />
        ) : (
          <>
            <div
              className={buildClassName(tabListStyles.container, receiveStyles.tabs, styles.tabs)}
              role="tablist"
              aria-label={title}
            >
              {(['counterparty', 'category', 'wallets'] as const).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  role="tab"
                  aria-selected={tab === kind}
                  className={buildClassName(tabStyles.Tab, receiveStyles.tab, styles.tab,
                    tab === kind && tabStyles.Tab_active)}
                  onClick={() => {
                    setTab(kind);
                    setQuery('');
                  }}
                >
                  <span className={buildClassName(tabStyles.content, styles.tabContent,
                    tab === kind && styles.tabSelected)}
                  >
                    {lang(kind === 'counterparty' ? 'Counterparties'
                      : kind === 'category' ? 'Categories' : 'Addresses')}
                  </span>
                </button>
              ))}
            </div>
            {tab === 'wallets' ? (
              <SettingsMetadataSharing
                key={`${accountId}:${accounts?.[accountId]?.byChain.tron?.address ?? ''}`}
                accountId={accountId}
                accounts={accounts}
                accountIds={directory.accountIds}
                isActive={isActive}
              />
            ) : (
              <>
                <div className={buildClassName(searchStyles.wrapper, styles.search)} data-telemetry-block="">
                  <div className={searchStyles.iconAddon}>
                    <i className={buildClassName(searchStyles.icon, 'icon-search')} aria-hidden />
                  </div>
                  <input
                    id="metadata-directory-search"
                    className={searchStyles.input}
                    type="search"
                    value={query}
                    placeholder={lang('Search')}
                    aria-label={lang('Search')}
                    autoComplete="off"
                    onInput={(event: FormEvent<HTMLInputElement>) => setQuery(event.currentTarget.value)}
                  />
                </div>
                <div className={settingsStyles.blockTitleRow}>
                  <h3 className={settingsStyles.blockTitle}>
                    {lang(tab === 'counterparty' ? 'Counterparties' : 'Categories')}
                  </h3>
                  <button
                    type="button"
                    className={buildClassName(styles.iconButton, styles.sectionButton)}
                    title={lang('Add')}
                    aria-label={lang('Add')}
                    disabled={!directory.accountIds.includes(accountId)}
                    onClick={() => openEditor({ kind: tab })}
                  ><i className="icon-plus" aria-hidden />
                  </button>
                </div>
                <div className={settingsStyles.block}>
                  {filtered.map((record) => (
                    <button
                      key={`${record.owner_address}:${record.id}`}
                      type="button"
                      className={buildClassName(settingsStyles.item, styles.record)}
                      data-telemetry-block=""
                      onClick={() => openEditor({ kind: tab, record })}
                    >
                      {tab === 'counterparty'
                        ? <CounterpartyAvatar counterparty={record as ApiWalletTransactionCounterparty} />
                        : <MetadataCategoryIcon {...record as ApiWalletTransactionCategory} />}
                      <span className={buildClassName(settingsStyles.itemContent, styles.recordContent)}>
                        <span className={settingsStyles.itemTitle}>{record.name}</span>
                        {tab === 'counterparty' && (
                          <span className={settingsStyles.itemSubtitle}>
                            {(record as ApiWalletTransactionCounterparty).addresses
                              ?.map((address) => shortenAddress(address)).join(' \u00b7 ')
                              || lang('$metadata_no_addresses')}
                          </span>
                        )}
                      </span>
                      <i
                        className={buildClassName(settingsStyles.iconChevronRight, 'icon-chevron-right')}
                        aria-hidden
                      />
                    </button>
                  ))}
                  {!filtered.length && (
                    <div className={styles.empty}>
                      {directory.isLoading ? <Spinner /> : lang(textQuery ? '$metadata_no_matches' : '$metadata_empty')}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default memo(withGlobal<OwnProps>((global): StateProps => ({
  accountId: selectCurrentAccountId(global) ?? '',
  accounts: selectAccounts(global),
}))(SettingsMetadata));
